



















































import { Component, Mixins, Prop } from "vue-property-decorator";
import { ModelEditableMixin, NumberInput } from "../../../../../base/components/system";
import { OptionDropDownSelector } from "../../../../../base/components/system/selectors";
import { StandardOption } from "../../../../../base/components/system/selectors/mixins";
import { CommonComponentsContainer } from "../../../../../formatEditor/components/format/editors/contents";
import { Questionnaire, QuestionnaireExtension, QuestionnaireType, QuestionnaireCodeableConcept, QuestionnaireCoding, QuestionnaireExpression, QuestionnaireOption } from "../../../../domain/format/objects";
import { UuidFactory } from "../../../../../base/utilities";
import { DomainRegistry } from "../../../../../formatEditor/domain";

@Component({
  components: {
    OptionDropDownSelector,
    NumberInput,
  }
})
export default class QuestionnaireEditor extends Mixins(ModelEditableMixin) {
  writePropertyWhenValid: (writer: (source: Questionnaire) => void) => void;
  readPropertyOrDefault: <T>(reader: (source: Questionnaire) => T, defaultValue: T) => T;

  @Prop()
  selectableTypes: (QuestionnaireType | undefined)[];

  @Prop({ default: false })
  isDisplayEditable: boolean;

  @Prop({ default: false })
  isCodeEditable: boolean;

  @Prop()
  commonComponents: CommonComponentsContainer;

  get _chlidGuids(): string[] {
    return this.readPropertyOrDefault(s => s._childGuids, []);
  }

  get selectableTypeOptions(): StandardOption<QuestionnaireType | undefined>[] {
    return this.selectableTypes
      ? this.selectableTypes.map(t => new StandardOption<QuestionnaireType | undefined>(t ? t : "", t))
      : [];
  }

  get isTypeSelectable(): boolean {
    return 1 < this.selectableTypeOptions.length;
  }

  get type(): QuestionnaireType | undefined {
    return this.readPropertyOrDefault(s => s.type, undefined);
  }

  set type(value: QuestionnaireType | undefined) {
    this.writePropertyWhenValid(s => {
      s.type = value;
      if (value !== "group") {
        s.gtable = undefined;
      }
    });
  }

  get isTypeSelected(): boolean {
    return !!this.type;
  }

  get isGroupTypeSelectable(): boolean {
    return this.isTypeSelectable && this.selectableTypes.some(t => t === "group");
  }

  get isGroupTypeSelected(): boolean {
    return this.type === "group";
  }

  get isGtable(): boolean {
    return this.readPropertyOrDefault(s => !!s.gtable, false);
  }

  set isGtable(value: boolean) {
    this.writePropertyWhenValid(s => s.gtable = value ? this.gtableExtension.clone() : undefined);
  }

  private gtableExtension = new QuestionnaireExtension(
    "http://h17.org/fhir/StructureDefinition/questionnaire-itemControl",
    undefined,
    new QuestionnaireCodeableConcept([new QuestionnaireCoding("gtable", "http://h17.org/fhir/questionnaire-item-control", "Group Table")], "Group Table")
  );

  get repeats(): boolean {
    return this.readPropertyOrDefault(s => !!s.repeats, undefined);
  }

  set repeats(value: boolean) {
    this.writePropertyWhenValid(s => s.repeats = value ? true : undefined);
  }

  get maxOccurs(): number {
    return this.readPropertyOrDefault(s => s.maxOccurs, undefined);
  }

  set maxOccurs(value: number) {
    this.writePropertyWhenValid(s => s.maxOccurs = value);
  }

  get linkId(): string {
    return this.readPropertyOrDefault(s => s.linkId, undefined);
  }

  set linkId(value: string) {
    this.writePropertyWhenValid(s => {
      s.linkId = value;
      if (!s._guid) {
        s._guid = UuidFactory.create();
      }
    });
  }

  get text(): string {
    return this.readPropertyOrDefault(s => s.text, undefined);
  }

  set text(value: string) {
    this.writePropertyWhenValid(s => s.text = value);
  }

  get fhirPath(): QuestionnaireExtension {
    return this.readPropertyOrDefault(s => s.fhirPath, undefined);
  }

  private writeFhirPathPropertyWhenValid(writer: (source: QuestionnaireExtension) => void): void {
    // FHIRパスのlanguageは常にtext/fhirpath.指定されてない場合のsourceはlanguageを初期化したものとする.
    const source = this.fhirPath ? this.fhirPath.clone() : new QuestionnaireExtension("", new QuestionnaireExpression("", "", "text/fhirpath"));
    writer(source);
    // 必要なパラメータが指定されてない場合はfhirPath未指定とする.
    this.writePropertyWhenValid(s => s.fhirPath = (source.url || source.valueExpression.name || source.valueExpression.expression) ? source : undefined);
  }

  private readFhirPathPropertyOrDefault<T>(reader: (source: QuestionnaireExtension) => T, defaultValue: T): T {
    return this.fhirPath
      ? reader(this.fhirPath)
      : defaultValue;
  }

  get fhirPathUrl(): string {
    return this.readFhirPathPropertyOrDefault(s => s.url, undefined);
  }

  set fhirPathUrl(value: string) {
    this.writeFhirPathPropertyWhenValid(s => s.url = value);
  }

  get fhirPathValueExpression(): QuestionnaireExpression {
    return this.readFhirPathPropertyOrDefault(s => s.valueExpression, undefined);
  }

  private writeFhirPathValueExpressionPropertyWhenValid(writer: (source: QuestionnaireExpression) => void): void {
    // FHIRパスのlanguageは常にtext/fhirpath.指定されてない場合のsourceはlanguageを初期化したものとする.
    const source = this.fhirPathValueExpression ? this.fhirPathValueExpression.clone() : new QuestionnaireExpression("", "", "text/fhirpath");
    writer(source);
    this.writeFhirPathPropertyWhenValid(s => s.valueExpression = source);
  }

  private readFhirPathValueExpressionPropertyOrDefault<T>(reader: (source: QuestionnaireExpression) => T, defaultValue: T): T {
    return this.fhirPathValueExpression
      ? reader(this.fhirPathValueExpression)
      : defaultValue;
  }

  get fhirPathValueExpressionName(): string {
    return this.readFhirPathValueExpressionPropertyOrDefault(s => s.name, undefined);
  }

  set fhirPathValueExpressionName(value: string) {
    this.writeFhirPathValueExpressionPropertyWhenValid(s => s.name = value);
  }

  get fhirPathValueExpressionExpression(): string {
    return this.readFhirPathValueExpressionPropertyOrDefault(s => s.expression, undefined);
  }

  set fhirPathValueExpressionExpression(value: string) {
    this.writeFhirPathValueExpressionPropertyWhenValid(s => s.expression = value);
  }

  get display(): string {
    return this.readPropertyOrDefault(s => s.display, undefined);
  }

  set display(value: string) {
    this.writePropertyWhenValid(s => s.display = value);
  }

  get code(): string {
    return this.readPropertyOrDefault(s => s.code, undefined);
  }

  set code(value: string) {
    this.writePropertyWhenValid(s => s.code = value);
  }

  async showGlossary(): Promise<void> {
    const selected = await this.commonComponents.getDialogs().getQuestionnaireGlossaryItemSelectDialog().show(null, this.selectableTypes, this.type);
    if (selected) {
      const choiceItems = selected.type === "choice"
        ? await DomainRegistry.instance.questionnaire.getGlossaryRepository().GetGlossaryChoiceItems(selected.linkId)
        : undefined;
      this.writePropertyWhenValid(s => {
        s.type = selected.type;
        s.linkId = selected.linkId;
        if (choiceItems) {
          s.answerOption = choiceItems.sort((a, b) => a.displayOrder - b.displayOrder).map(c => QuestionnaireOption.from({
            valueCoding: {
              code: c.code,
              display: c.display,
            }
          }));
        }
      });
    }
  }
}
